<script setup lang="ts">
import type { ProductContentsResponseSchema } from '@laam/cms-shared';
import { computed, onBeforeMount, provide } from 'vue';
import { isMobileView } from '../../../utils/helpers.ts';
import { LazyImage } from '../../lazy-image';
import { MediaType, VideoType } from './types';
import { useMute } from './utils.ts';
import VideoPlayer from './VideoPlayer.vue';

interface Props {
	videoUrl?: string;
	src: string;
	alt: string;
	type: MediaType;
	isActive: boolean;
	poster?: string;
	reels?: boolean;
	contentData?: ProductContentsResponseSchema['contents'][number];
	isProductVideo?: boolean;
}
const props = defineProps<Props>();
const { isMuted, resetMute } = useMute();
const videoType = computed(() => {
	if (props.type !== MediaType.VIDEO) return;
	return props.src.includes('.m3u8') ? VideoType.HLS : VideoType.CONVENTIONAL;
});
provide('contentData', props.contentData);
provide('isProductVideo', props.isProductVideo);

onBeforeMount(() => {
	resetMute();
});
</script>

<template>
	<LazyImage
		v-if="type === MediaType.IMAGE"
		:src="src"
		:alt="alt"
		:width="isMobileView() ? 300 : 500"
		class="lg:rounded-xl"
		loading="lazy"
	/>

	<div
		v-if="type === MediaType.VIDEO"
		class="relative flex justify-center lg:rounded-xl overflow-clip size-full"
		:class="{
			'aspect-[9/16]': reels,
			'aspect-[5/7]': !reels,
		}"
	>
		<LazyImage
			v-if="poster"
			:src="poster"
			:alt="alt"
			:width="isMobileView() ? 300 : 500"
			class="object-cover blur-md"
			loading="lazy"
		/>
		<VideoPlayer
			v-show="isActive"
			class="absolute size-full flex items-center justify-center"
			:video-url="src"
			:class="{
				'object-cover': reels,
			}"
			:src="src"
			:poster="poster"
			:is-playing="isActive"
			:video-type="videoType"
			:is-muted="isMuted"
		/>
	</div>
</template>
